import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILE_CATEGORIES } from "@/components/badges";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function EditRequestDialog({ req, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { selectedId } = useProject();
  const [editReqForm, setEditReqForm] = useState({ title: "", description: "", category: "Other", due_date: "" });

  useEffect(() => {
    if (req) setEditReqForm({ title: req.title, description: req.description || "", category: req.category, due_date: req.due_date });
  }, [req]);

  const editReqMut = useMutation({
    mutationFn: async () => (await api.put(`/file-requests/${req.id}`, editReqForm)).data,
    onSuccess: () => {
      toast.success(t("filesPage.toastUpdated"));
      onClose();
      qc.invalidateQueries({ queryKey: ["file-requests", selectedId] });
      qc.invalidateQueries({ queryKey: ["documents", selectedId] });
      qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-request-dialog">
        <DialogHeader><DialogTitle>{t("filesPage.editTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("common.title")}</Label>
            <Input value={editReqForm.title} onChange={(e) => setEditReqForm({ ...editReqForm, title: e.target.value })} data-testid="edit-request-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.description")}</Label>
            <RichTextEditor value={editReqForm.description} onChange={(v) => setEditReqForm({ ...editReqForm, description: v })} testId="edit-request-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("common.category")}</Label>
              <Select value={editReqForm.category} onValueChange={(v) => setEditReqForm({ ...editReqForm, category: v })}>
                <SelectTrigger data-testid="edit-request-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FILE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.dueDate")}</Label>
              <Input type="date" value={editReqForm.due_date} onChange={(e) => setEditReqForm({ ...editReqForm, due_date: e.target.value })} data-testid="edit-request-due-date-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => editReqMut.mutate()} disabled={!editReqForm.title.trim() || !editReqForm.due_date || editReqMut.isPending} data-testid="edit-request-submit-button">
            {editReqMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
